import React from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';

import OutputText from '@ey/canvascoreservices/OutputText';

import MotifIcon from '@ey/motif-react-wrapper/Icon';
import {IconoirMail, IconoirPhoneSolid} from '@ey-xd/motif-icon';

import {labels, currentResource} from '../../../../util/utils';
import selectAccessPermissions from '../../../../reducers/Project/selectors/selectAccessPermissions';

import ColoredChip from '../../../Common/ColoredChip';

import DashboardCheck from '../DashboardCheck';
import getAvailableEntityList from '../../../Pages/ContactDB/Toolbar/GenerateMessage/GMForm/utils/getAvailableEntityList';
import normalizeContactList from '../../../Pages/ContactDB/Toolbar/GenerateMessage/GMForm/utils/normalizeContactList';

import Modal from '../../../CoreWrappers/Modal';
import User from '../../../CoreWrappers/User';
import Button from '../../../CoreWrappers/Button';
import Card from '../../../CoreWrappers/Card';
import StyledModalBody from './StyledModalBody';
import Chip from '../../../CoreWrappers/Chip';

const ProfileModal = ({onCloseModal, profile}) => {
	const contactList = useSelector((store) =>
		store.contacts?.entities?.length > 0
			? normalizeContactList(getAvailableEntityList(store.contacts.entities, false))
			: []
	);
	const {groupRiskAssessmentAccess} = selectAccessPermissions();
	const entitiesAvailable = contactList.filter(({emailAddress}) => emailAddress === profile.emailAddress);
	const renderDashboardChip = (text) => text && <Chip type="outlined">{text}</Chip>;

	const renderTag = (text, color = '', textProps = {}) =>
		text && (
			<ColoredChip containerClassName="max-half" className={color?.toLowerCase()}>
				<OutputText body3 {...textProps}>
				{text}
				</OutputText>
			</ColoredChip>
		);

	const mapToDashboard = (dashboardKey) => {
		const lowercaseKey = dashboardKey?.toLowerCase()?.replaceAll(' ', '');
		const dashboard = _.find(currentResource.dashboardContactViews, (dashboard) => {
			const dashboardValue = dashboard?.value?.toLowerCase().replaceAll(' ', '');
			return lowercaseKey === dashboardValue || lowercaseKey === `ey${dashboardValue}`;
		}) || {id: Number.MAX_VALUE, value: dashboardKey};
		if (dashboardKey?.startsWith('EY ')) return {...dashboard, value: dashboardKey};

		return dashboard;
	};

	const renderDashboardTag = (dashboard) => renderDashboardChip(dashboard.key);

	const renderEmail = (email) => {
		const props = {
			svg: IconoirMail,
			iconSize: 20,
			variant: 'text-alt',
			label: email,
			onClick: () => {
				location.href = `mailto:${email}`;
			}
		};
		return email && <Button {...props} />;
	};

	const renderPhone = (phone) =>
		phone && (
			<span className="contact-info phone icon-text">
				<MotifIcon src={IconoirPhoneSolid} />
				<OutputText>{phone}</OutputText>
			</span>
		);

	const renderPersonalData = () => {
		const {isInternal, name, lastName, emailAddress, location, phone, serviceLine, roleName} = profile;
		const props = {
			firstName: name,
			lastName,
			email: emailAddress,
			isInternal,
			showInfo: false,
			showAside: false,
			size: 'xl'
		};
		return (
			<section className="modal-section contact-data">
				<div className="user-profile">
					<User {...props} />
					<div className="personal-information">
						<OutputText>
							{name} {lastName}
						</OutputText>
						{isInternal && location && <OutputText>{location}</OutputText>}
						<div>
							{renderEmail(profile.emailAddress)}
							{isInternal && renderPhone(phone)}
						</div>
					</div>
				</div>
				<div className="rol">
					{
						<OutputText body2 light>
							{serviceLine} {roleName}{' '}
						</OutputText>
					}
					<div className="circulo"></div>
					{
						<OutputText body2 light>
							{isInternal ? labels.internal : labels.external}{' '}
						</OutputText>
					}
				</div>
			</section>
		);
	};

	const renderPropValue = (value, valueType) => {
		switch (valueType) {
			case 'boolean':
				return <DashboardCheck condition={value} />;
			default:
				return (
					<OutputText title={value} className="prop-value" body2 ellipsis>
						{value}
					</OutputText>
				);
		}
	};

	const renderProp = (label, value, valueType = 'text') => {
		const title = `${label} `;
		return (
			<div className="flex-1-inline-horizontal">
				<OutputText className="prop" title={title} body2 light ellipsis>
					{title}
				</OutputText>
				{renderPropValue(value, valueType)}
			</div>
		);
	};

	const renderAccess = () => {
		const {dashboard, engagements, contactList, groupRiskAssessment} = profile;
		const hasDashboards = !!dashboard?.length;
		const entitiesCount = entitiesAvailable[0]?.entities?.length || 0;
		const entitiesLabel = entitiesCount === 1 ? labels.entityLabel : labels.entitiesLabel;
		const engagementsLabel = engagements.length === 1 ? labels.engagementLabel : labels.engagementsLabel;

		return (
			<Card className="card-content">
				<span className="modal-section">
					<OutputText className="section-title" size="XL">
						{labels.profileModalAccessSection}
					</OutputText>
					<div className="group-access">
						{renderProp(entitiesLabel, entitiesCount)}
						{renderProp(engagementsLabel, engagements.length)}
						{renderProp(labels.dashboard, hasDashboards, 'boolean')}
						{groupRiskAssessmentAccess &&
							renderProp(labels.groupRiskAssessment, groupRiskAssessment?.hasAccess, 'boolean')}
						{renderProp(labels.contactList, contactList.hasAccess, 'boolean')}
					</div>
				</span>
			</Card>
		);
	};

	const renderCustomAttributes = () => {
		const {customColumns} = profile;
		const hasCustomAttributes = customColumns?.length;
		return (
			(hasCustomAttributes && (
				<Card className="card-content">
					<span className="modal-section section-custom-attributes">
						<OutputText className="section-title" size="XXL">
							{labels.profileModalCustomAttributesSection}
						</OutputText>
						<div className="prop-group customColumns">
							{_.sortBy(customColumns ?? [], 'columnName').map((c) =>
								renderProp(c.name, c?.options[0]?.option, c.columnType)
							)}
						</div>
					</span>
				</Card>
			)) ||
			''
		);
	};

	const renderTags = () => {
		const {tags} = profile;
		const hasTags = tags?.length;
		return (
			(hasTags && (
				<Card className="card-content">
					<span className="modal-section section-custom-attributes">
						<OutputText className="section-title" size="XXL">
							{labels.columnTags}
						</OutputText>
						{(tags.length && (
							<div className="chip-group">
								{_.sortBy(tags, ['name']).map(({name, color}) => {
									return renderTag(name, color);
								})}
							</div>
						)) ||
							''}
					</span>
				</Card>
			)) ||
			''
		);
	};

	const renderDashboards = () => {
		const {dashboard} = profile;
		const hasDashboards = dashboard?.length;
		return (
			(hasDashboards && (
				<Card className="card-content">
					<span className={`modal-section`}>
						<OutputText className="section-title" size="XL">
							{labels.profileModalDashboardSection}
						</OutputText>
						<div className="chip-group">
							{_.sortBy(dashboard.map(mapToDashboard), ['id', 'value']).map(renderDashboardTag)}
						</div>
					</span>
				</Card>
			)) ||
			''
		);
	};

	const props = {
		show: true,
		onHide: onCloseModal,
		modalsize: 'small',
		title: labels.profileModalTitle,
		closeTitle: labels.closeLabel,
		showFooter: false
	};

	return (
		<Modal {...props}>
			<StyledModalBody>
				{renderPersonalData()}
				{renderAccess()}
				{renderDashboards()}
				{renderTags()}
				{renderCustomAttributes()}
			</StyledModalBody>
		</Modal>
	);
};

export default ProfileModal;

ProfileModal.defaultProps = {};

ProfileModal.propTypes = {
	onCloseModal: PropTypes.func.isRequired,
	profile: PropTypes.any
};
